import { create } from "zustand";
import type { BlockType, Camera, Point } from "@worktools/shared";

export type Tool = "select" | "pan" | "pen" | "marker" | "eraser" | "grant";

export type ContextMenuTarget =
  | { kind: "canvas"; canvasPoint: Point }
  | { kind: "block"; blockId: string; canvasPoint: Point };

export type ContextMenuState = { screen: Point; target: ContextMenuTarget } | null;

export type SpotlightInvite = {
  fromId: string;
  fromName: string;
  color: string;
  camera: Camera;
  blockIds: string[];
  at: number;
} | null;

type UiState = {
  camera: Camera;
  selection: string[];
  tool: Tool;
  penColor: string;
  penWidth: number;
  contextMenu: ContextMenuState;
  followingId: string | null;
  spotlightInvite: SpotlightInvite;
  snapToGrid: boolean;
  editingBlockId: string | null;
  paletteDrag: BlockType | null;

  setCamera: (camera: Camera | ((current: Camera) => Camera)) => void;
  panBy: (delta: Point) => void;
  setSelection: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  setTool: (tool: Tool) => void;
  setPen: (patch: { color?: string; width?: number }) => void;
  openContextMenu: (menu: NonNullable<ContextMenuState>) => void;
  closeContextMenu: () => void;
  setFollowing: (id: string | null) => void;
  setSpotlightInvite: (invite: SpotlightInvite) => void;
  setSnapToGrid: (value: boolean) => void;
  setEditingBlockId: (id: string | null) => void;
  setPaletteDrag: (type: BlockType | null) => void;
  resetForBoard: () => void;
};

export const useUi = create<UiState>((set) => ({
  camera: { x: 0, y: 0, zoom: 1 },
  selection: [],
  tool: "select",
  penColor: "#0a0a0a",
  penWidth: 3,
  contextMenu: null,
  followingId: null,
  spotlightInvite: null,
  snapToGrid: false,
  editingBlockId: null,
  paletteDrag: null,

  setCamera: (camera) =>
    set((state) => ({
      camera: typeof camera === "function" ? camera(state.camera) : camera,
    })),
  panBy: (delta) =>
    set((state) => ({
      camera: { ...state.camera, x: state.camera.x + delta.x, y: state.camera.y + delta.y },
    })),
  setSelection: (ids) => set({ selection: ids }),
  toggleSelection: (id) =>
    set((state) => ({
      selection: state.selection.includes(id)
        ? state.selection.filter((item) => item !== id)
        : [...state.selection, id],
    })),
  clearSelection: () => set({ selection: [] }),
  setTool: (tool) => set({ tool }),
  setPen: (patch) => set((state) => ({ penColor: patch.color ?? state.penColor, penWidth: patch.width ?? state.penWidth })),
  openContextMenu: (contextMenu) => set({ contextMenu }),
  closeContextMenu: () => set({ contextMenu: null }),
  setFollowing: (followingId) => set({ followingId }),
  setSpotlightInvite: (spotlightInvite) => set({ spotlightInvite }),
  setSnapToGrid: (snapToGrid) => set({ snapToGrid }),
  setEditingBlockId: (editingBlockId) => set({ editingBlockId }),
  setPaletteDrag: (paletteDrag) => set({ paletteDrag }),
  resetForBoard: () =>
    set({
      camera: { x: 0, y: 0, zoom: 1 },
      selection: [],
      contextMenu: null,
      followingId: null,
      spotlightInvite: null,
      editingBlockId: null,
      tool: "select",
    }),
}));
